<template>
    <section class="register__widget-row country-state">
        <div :disabled="disableCountryDropdown"
            :class="['label country', { 'label--loading': disableCountryDropdown }]"
        >
            {{ $t('registerMobileNumber.country') }}:

            <country-dropdown :country="innerValue.country"
                :disabled="disableCountryDropdown"
                :placeholder="$t('kycLevel2PostalAddress.countryHint')"
                @update:country="onCountryChanged"
            />
        </div>
        <label v-if="showRestrictedCountryMessage"
            class="label__error"
        >
            {{ $t('registerMobileNumber.countryBlocked') }}
        </label>
        <div v-show="showStatesInput"
            class="label label--no-bottom-padding state"
        >
            {{ $t('registerMobileNumber.state') }}:

            <multiselect v-model="innerValue.state.display"
                :options="states"
                :allow-empty="false"
                :show-labels="false"
                track-by="display"
                label="display"
                :placeholder="$t('registerMobileNumber.stateHint')"
                :class="['label__input', { 'label__input--active': innerValue.state.display }]"
                @input="onStateChanged"
            >
                <template slot="singleLabel"
                    slot-scope="props"
                >
                    {{ props.option.display ? props.option.display : innerValue.state.display }}
                </template>
                <template slot="option"
                    slot-scope="props"
                >
                    {{ props.option.display }}
                </template>
                <span slot="noResult">{{ $t('registerMobileNumber.noResultsCountries') }}</span>
            </multiselect>
        </div>
    </section>
</template>

<script>
// Store
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Components
import countryDropdown from '@webapp/components/country-dropdown/country-dropdown.vue';

// Locale Modules
import localStoreTypes from '@webapp/store/types.js';

export default {
    name: 'country-state-dropdown',
    components: {
        countryDropdown,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        prepopulatedCountry: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            states: [],
            innerValue: {
                canRegister: false,
                hasStates: false,
                country: null,
                state: {},
            },
            loadingCountries: false,
            fetchingStates: false,
        };
    },
    computed: {
        ...mapGetters('countries', ['countriesList', 'getStates']),

        ...mapGetters('auth', ['getCountryRegistrationRestrictions']),

        showStatesInput() {
            return this.value?.hasStates && this.states?.length;
        },

        showRestrictedCountryMessage() {
            return !this.loadingCountries && this.value?.country.name && !this.value.canRegister;
        },

        disableCountryDropdown() {
            return this.loadingCountries || this.fetchingStates;
        },
    },
    watch: {
        innerValue: {
            handler: function(newValue) {
                this.$emit('input', newValue);
            },
            deep: true,
        },
    },
    created() {
        this.setPrepopulatedCountry();
    },
    methods: {
        ...mapActions({
            setRegistration: localStoreTypes.SET_REGISTRATION,
        }),

        ...mapActions('auth', ['fetchCountryRegistrationRestrictions']),

        ...mapActions('countries', ['fetchCountryStates']),

        async onCountryChanged(country) {
            this.loadingCountries = true;

            await this.fetchCountryRestrictions(country);
            await this.setCountryStates(country);

            this.innerValue = {
                canRegister: this.getCountryRegistrationRestrictions,
                hasStates: Boolean(this.states && this.states.length),
                state: {},
                country,
            };

            this.setRegistration([
                {
                    property: 'countryIso2',
                    value: country.iso2,
                },
                {
                    property: 'stateCode',
                    value: '',
                },
            ]);
            this.loadingCountries = false;
        },

        onStateChanged(selectedState) {
            this.innerValue.state = selectedState;
            this.setRegistration([
                {
                    property: 'stateCode',
                    value: selectedState.code,
                },
            ]);
        },

        async fetchCountryRestrictions(country) {
            await this.fetchCountryRegistrationRestrictions({
                countryId: country.id,
            });
        },

        async setCountryStates(country) {
            this.fetchingStates = true;
            await this.fetchCountryStates({
                countryId: country.id,
            });

            this.states = this.getStates.map((stateData) => ({
                ...stateData,
                display: stateData.state,
            }));

            this.fetchingStates = false;
        },

        setPrepopulatedCountry() {
            if (this.prepopulatedCountry) {
                const userCountry = this.countriesList.find((country) => country.name === this.prepopulatedCountry);

                this.onCountryChanged(userCountry);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
    .register__widget-row {
        .state {
            margin-top: 4rem;
        }

        .country {
            margin-bottom: 4px;
        }
    }
</style>